import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowLeft, FaPlus, FaPencilAlt, FaBan, FaCheck, FaSearch, FaSave,
  FaChalkboardTeacher, FaUserGraduate, FaUserFriends, FaChevronLeft, FaChevronRight,
} from "react-icons/fa";
import Modal from "../Modal";

const API = "/api";
const REGISTROS_POR_PAGINA = 8;

const AsistenciaDominical = () => {

  const [clases, setClases] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [personas, setPersonas] = useState([]);

  const [claseSeleccionada, setClaseSeleccionada] = useState("");
  const [servicioSeleccionado, setServicioSeleccionado] = useState("");
  const [estatusFiltro, setEstatusFiltro] = useState("activos");
  const [busqueda, setBusqueda] = useState("");
  const [pagina, setPagina] = useState(1);

  const [nombreNueva, setNombreNueva] = useState("");
  const [tipoNueva, setTipoNueva] = useState(2);
  const [claseNueva, setClaseNueva] = useState("");

  const [modoEdicion, setModoEdicion] = useState(false);
  const [personaEditar, setPersonaEditar] = useState(null);

  const [mostrarModal, setMostrarModal] = useState(false);

  const [notificacion, setNotificacion] = useState({
    mostrar: false,
    mensaje: "",
    tipo: "success",
  });

  const mostrarToast = (mensaje, tipo = "success") => {
    setNotificacion({ mostrar: true, mensaje, tipo });
    setTimeout(() => setNotificacion({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const cargarClases = async () => {
    const res = await fetch(`${API}/dominical/clases`, { credentials: "include" });
    const data = await res.json();
    setClases(data || []);
  };

  const cargarServicios = async () => {
    const res = await fetch(`${API}/dominical/servicios`, { credentials: "include" });
    const data = await res.json();
    setServicios(data || []);

    // La asistencia solo se toma los domingos: si existe el servicio de
    // Escuela Dominical de hoy, se selecciona automáticamente.
    const hoy = new Date();
    const servicioDeHoy = (data || []).find((s) => {
      const f = new Date(s.fecha);
      return (
        f.getUTCFullYear() === hoy.getFullYear() &&
        f.getUTCMonth() === hoy.getMonth() &&
        f.getUTCDate() === hoy.getDate()
      );
    });
    if (servicioDeHoy) setServicioSeleccionado(servicioDeHoy.id_servicio);
  };

  const cargarAsistencia = useCallback(async () => {
    if (!claseSeleccionada || !servicioSeleccionado) {
      setPersonas([]);
      return;
    }

    const res = await fetch(
      `${API}/dominical/asistencia/${servicioSeleccionado}/${claseSeleccionada}?estatus=${estatusFiltro}`,
      { credentials: "include" }
    );

    const data = await res.json();

    const lista = (data || []).map((p) => ({
      ...p,
      asistio: p.asistio ?? false,
    }));

    setPersonas(lista);
  }, [claseSeleccionada, servicioSeleccionado, estatusFiltro]);

  useEffect(() => {
    cargarClases();
    cargarServicios();
  }, []);

  useEffect(() => {
    setPagina(1);
    cargarAsistencia();
  }, [cargarAsistencia]);

  const cambiarAsistencia = (id) => {
    setPersonas(personas.map((p) => (p.id === id ? { ...p, asistio: !p.asistio } : p)));
  };

  const guardar = async () => {
    const asistencias = personas.map((p) => ({
      persona_id: p.id,
      asistio: p.asistio,
    }));

    try {
      const res = await fetch(`${API}/dominical/asistencia`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          servicio_id: servicioSeleccionado,
          clase_id: claseSeleccionada,
          asistencias,
        }),
      });

      if (!res.ok) {
        mostrarToast("Error guardando la asistencia", "error");
        return;
      }

      mostrarToast("Asistencia guardada correctamente");
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const limpiarModal = () => {
    setNombreNueva("");
    setTipoNueva(2);
    setClaseNueva(claseSeleccionada);
    setModoEdicion(false);
    setPersonaEditar(null);
  };

  const abrirModalAgregar = () => {
    if (!claseSeleccionada) {
      mostrarToast("Seleccione una clase primero", "error");
      return;
    }

    limpiarModal();
    setMostrarModal(true);
  };

  const editarPersona = (persona) => {
    setModoEdicion(true);
    setPersonaEditar(persona);

    setNombreNueva(persona.nombre);
    setTipoNueva(persona.tipo);
    setClaseNueva(claseSeleccionada);

    setMostrarModal(true);
  };

  const agregarPersona = async () => {
    if (!nombreNueva.trim()) {
      mostrarToast("Ingrese nombre", "error");
      return;
    }

    try {
      const res = await fetch(`${API}/dominical/persona`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: nombreNueva,
          tipo: parseInt(tipoNueva),
          clase_id: claseNueva,
        }),
      });

      if (!res.ok) {
        mostrarToast("Error agregando persona", "error");
        return;
      }

      setMostrarModal(false);
      mostrarToast("Persona agregada correctamente");
      cargarAsistencia();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const actualizarPersona = async () => {
    try {
      const res = await fetch(`${API}/dominical/persona/${personaEditar.id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nombre: nombreNueva,
          tipo: parseInt(tipoNueva),
          clase_id: claseNueva,
        }),
      });

      if (!res.ok) {
        mostrarToast("Error actualizando persona", "error");
        return;
      }

      setMostrarModal(false);
      mostrarToast("Persona actualizada correctamente");
      cargarAsistencia();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const cambiarEstado = async (persona) => {
    const activar = !persona.activo;
    try {
      const res = await fetch(`${API}/dominical/persona/${persona.id}${activar ? "/reactivar" : ""}`, {
        method: activar ? "PATCH" : "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        mostrarToast("Error actualizando el estatus", "error");
        return;
      }

      mostrarToast(activar ? "Persona reactivada" : "Persona desactivada");
      cargarAsistencia();
    } catch (error) {
      console.error(error);
      mostrarToast("Error inesperado", "error");
    }
  };

  const personasFiltradas = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return personas;
    return personas.filter((p) => p.nombre?.toLowerCase().includes(termino));
  }, [personas, busqueda]);

  const totalPaginas = Math.ceil(personasFiltradas.length / REGISTROS_POR_PAGINA);
  const inicio = (pagina - 1) * REGISTROS_POR_PAGINA;
  const personasPagina = personasFiltradas.slice(inicio, inicio + REGISTROS_POR_PAGINA);
  const inicioRango = personasFiltradas.length === 0 ? 0 : inicio + 1;
  const finRango = Math.min(inicio + REGISTROS_POR_PAGINA, personasFiltradas.length);

  const maestros = personas.filter((p) => p.tipo === 1 && p.asistio).length;
  const alumnos = personas.filter((p) => p.tipo === 2 && p.asistio).length;
  const invitados = personas.filter((p) => p.tipo === 3 && p.asistio).length;
  const total = personas.filter((p) => p.asistio).length;

  const badgeTipo = (tipo) => {
    if (tipo === 1) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600">Maestro</span>;
    if (tipo === 2) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600">Alumno</span>;
    if (tipo === 3) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">Visitante</span>;
    return null;
  };

  return (
    <div className="p-6 bg-slate-50">

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/asistencia" className="hover:text-indigo-600 flex items-center gap-1 inline-flex">
          <FaArrowLeft className="text-xs" /> Asistencia
        </Link>
      </div>

      {/* HEADER */}
      <div className="flex flex-wrap justify-between items-start gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Escuela Dominical</h1>
          <p className="text-sm text-slate-500 mt-0.5">Control de asistencia por clase y servicio.</p>
        </div>

        <button
          onClick={abrirModalAgregar}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
        >
          <FaPlus className="text-xs" /> Agregar persona
        </button>
      </div>

      {notificacion.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${notificacion.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {notificacion.mensaje}
        </div>
      )}

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Servicio</label>
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            value={servicioSeleccionado}
            onChange={(e) => setServicioSeleccionado(e.target.value)}
          >
            <option value="">Seleccione servicio</option>
            {servicios.map((s) => (
              <option key={s.id_servicio} value={s.id_servicio}>
                {new Date(s.fecha).toLocaleDateString("es-MX", { timeZone: "UTC" })}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Clase</label>
          <select
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            value={claseSeleccionada}
            onChange={(e) => setClaseSeleccionada(e.target.value)}
          >
            <option value="">Seleccione clase</option>
            {clases.map((c) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Estatus</label>
          <select
            value={estatusFiltro}
            onChange={(e) => setEstatusFiltro(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          >
            <option value="todos">Todos</option>
            <option value="activos">Activos</option>
            <option value="inactivos">Inactivos</option>
          </select>
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-slate-700 mb-1">Buscar</label>
          <FaSearch className="absolute left-3 top-[34px] text-slate-300 text-sm" />
          <input
            placeholder="Buscar por nombre..."
            value={busqueda}
            onChange={(e) => {
              setBusqueda(e.target.value);
              setPagina(1);
            }}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
      </div>

      {/* ESTADISTICAS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
          <p className="text-xs text-slate-500">Maestros</p>
          <p className="text-2xl font-bold text-indigo-600 mt-1">{maestros}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
          <p className="text-xs text-slate-500">Alumnos</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1">{alumnos}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
          <p className="text-xs text-slate-500">Visitantes</p>
          <p className="text-2xl font-bold text-amber-600 mt-1">{invitados}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 text-center">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{total}</p>
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left font-semibold">Estatus</th>
                <th className="px-4 py-3 text-center font-semibold">Asistencia</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>

            <tbody>
              {!claseSeleccionada || !servicioSeleccionado ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-slate-400">
                    Selecciona un servicio y una clase para ver la lista
                  </td>
                </tr>
              ) : personasPagina.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-10 text-slate-400">
                    Sin resultados
                  </td>
                </tr>
              ) : (
                personasPagina.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50 transition">
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2 font-medium text-slate-800">
                        <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                          {p.tipo === 1 ? <FaChalkboardTeacher className="text-xs" /> : p.tipo === 3 ? <FaUserFriends className="text-xs" /> : <FaUserGraduate className="text-xs" />}
                        </span>
                        {p.nombre}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">{badgeTipo(p.tipo)}</td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-2 text-slate-600">
                        <span className={`w-2 h-2 rounded-full ${p.activo ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {p.activo ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <input
                        type="checkbox"
                        checked={p.asistio}
                        onChange={() => cambiarAsistencia(p.id)}
                        className="w-5 h-5 accent-indigo-600 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => editarPersona(p)}
                          title="Editar"
                          className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                        >
                          <FaPencilAlt className="text-xs" />
                        </button>

                        {p.activo ? (
                          <button
                            onClick={() => cambiarEstado(p)}
                            title="Desactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition"
                          >
                            <FaBan className="text-xs" />
                          </button>
                        ) : (
                          <button
                            onClick={() => cambiarEstado(p)}
                            title="Reactivar"
                            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-200 transition"
                          >
                            <FaCheck className="text-xs" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      {personasFiltradas.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <span className="text-sm text-slate-500">
            {`Mostrando ${inicioRango} a ${finRango} de ${personasFiltradas.length} resultados`}
          </span>

          {totalPaginas > 1 && (
            <div className="flex items-center gap-2">
              <button
                disabled={pagina === 1}
                onClick={() => setPagina(pagina - 1)}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                <FaChevronLeft className="text-xs" />
              </button>

              <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">
                {pagina}
              </span>

              <button
                disabled={pagina === totalPaginas}
                onClick={() => setPagina(pagina + 1)}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
              >
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* GUARDAR */}
      {claseSeleccionada && servicioSeleccionado && personas.length > 0 && (
        <div className="mt-6">
          <button
            onClick={guardar}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition"
          >
            <FaSave className="text-xs" /> Guardar asistencia
          </button>
        </div>
      )}

      {/* MODAL */}
      <Modal
        open={mostrarModal}
        onClose={() => setMostrarModal(false)}
        title={modoEdicion ? "Editar persona" : "Agregar persona"}
      >
        <div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Nombre completo <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Nombre completo"
                value={nombreNueva}
                onChange={(e) => setNombreNueva(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Tipo</label>
              <select
                value={tipoNueva}
                onChange={(e) => setTipoNueva(e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
              >
                <option value={2}>Alumno</option>
                <option value={3}>Visitante</option>
                {modoEdicion && <option value={1}>Maestro</option>}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Clase</label>
              {modoEdicion ? (
                <select
                  value={claseNueva}
                  onChange={(e) => setClaseNueva(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                >
                  {clases.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              ) : (
                <div className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-slate-50 text-slate-600">
                  {clases.find((c) => String(c.id) === String(claseNueva))?.nombre || "—"}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-100">
            <button
              onClick={() => setMostrarModal(false)}
              className="px-3.5 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
            <button
              onClick={modoEdicion ? actualizarPersona : agregarPersona}
              className="px-3.5 py-1.5 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
            >
              Guardar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AsistenciaDominical;
